/**
 * ConsentVersionEditorTab (Plan 03-08, D7 / 5.0k) — Ley 8968 consent-version editor.
 *
 * Left column: language Select + Markdown Textarea + live ≥10-disclosures counter +
 * Save (Activar versión) + Descartar. Right column: live react-markdown preview.
 * Below: version history grouped by language, each row openable read-only.
 *
 * RBAC (D7): Save button gated by canMutate("consent_version", "write", role) — only
 * content_admin writes. Super-admin sees the editor read-only (Save disabled) but can
 * still browse the version history (backend GET allows super_admin). Backend remains
 * authoritative (POST 403 for super_admin); this is defense-in-depth + affordance-hiding.
 *
 * ≥10-disclosures gate (BUG-022 strict): the live counter uses the SAME regex the
 * backend Pydantic validator uses (`^\d+\.\s` multiline → 422 slug
 * consent_version_disclosures_insufficient), so the disabled-Save state and the
 * server-side 422 are a single source of truth. The 422 is the fallback if the gate
 * is somehow bypassed.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/lib/auth";
import { mapErrorSlug } from "@/lib/error-slug-mapper";
import { canMutate } from "@/lib/permissions";

import type {
  ConsentDocumentType,
  ConsentLanguage,
  ConsentVersionRow,
} from "./api";
import { ConsentVersionPreview } from "./ConsentVersionPreview";
import {
  useConsentVersionsQuery,
  useCreateConsentVersionMutation,
} from "./hooks";

const LANGUAGES: ConsentLanguage[] = ["es", "en", "de"];
// BUG-084 — Stage-1 application consent (Ley 8968) vs Stage-2 video consent (Ley 9877).
const DOCUMENT_TYPES: ConsentDocumentType[] = [
  "ley_8968_application",
  "ley_9877_video",
];
const MIN_DISCLOSURES = 10;
// SAME regex as backend consent_version.py field_validator (_NUMBERED_ITEM_RE).
const NUMBERED_ITEM_RE = /^\d+\.\s/gm;

export function ConsentVersionEditorTab() {
  const { t, i18n } = useTranslation("admin-settings");
  const role = useAuthStore((s) => s.user)?.role;
  const canWrite = canMutate("consent_version", "write", role);

  const [documentType, setDocumentType] =
    useState<ConsentDocumentType>("ley_8968_application");
  const [language, setLanguage] = useState<ConsentLanguage>("es");
  const [textMd, setTextMd] = useState("");
  const [viewVersion, setViewVersion] = useState<ConsentVersionRow | null>(null);

  const versionsQuery = useConsentVersionsQuery();
  const createMutation = useCreateConsentVersionMutation();

  // BUG-084 — the ≥10-disclosure gate applies ONLY to Ley-8968 application consent.
  // Ley-9877 video consent is a shorter legal instrument: only a non-empty-text floor
  // (mirrors the backend schema model_validator).
  const isApplicationConsent = documentType === "ley_8968_application";

  // Live ≥10-disclosures counter — count numbered list items (BUG-022 strict gate).
  const disclosureCount = useMemo(
    () => (textMd.match(NUMBERED_ITEM_RE) ?? []).length,
    [textMd],
  );
  const enoughDisclosures = disclosureCount >= MIN_DISCLOSURES;
  const textRulesPass = isApplicationConsent
    ? enoughDisclosures
    : textMd.trim().length > 0;

  const saveDisabled =
    !canWrite || createMutation.isPending || !textRulesPass;

  async function handleSave() {
    if (saveDisabled) return;
    try {
      await createMutation.mutateAsync({
        language_code: language,
        document_type: documentType,
        text_md: textMd,
      });
      toast.success(t("editor.save_success"));
      setTextMd("");
      setLanguage("es");
      setDocumentType("ley_8968_application");
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  function handleDiscard() {
    setTextMd("");
    setLanguage("es");
    setDocumentType("ley_8968_application");
  }

  // Group version history by language for the history list.
  const versionsByLanguage = useMemo(() => {
    const groups: Record<string, ConsentVersionRow[]> = {};
    for (const v of versionsQuery.data?.versions ?? []) {
      (groups[v.language_code] ??= []).push(v);
    }
    return groups;
  }, [versionsQuery.data]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("compliance.title")}</h2>
        <p className="text-sm text-[var(--talenta-text-muted)]">
          {t("compliance.subtitle")}
        </p>
      </div>

      {/* Editor + preview, side-by-side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor column */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold">{t("editor.title")}</h3>

          <div className="space-y-1">
            <label htmlFor="consent-doc-type" className="text-sm font-medium">
              {t("editor.document_type_label")}
            </label>
            <Select
              value={documentType}
              onValueChange={(v) => setDocumentType(v as ConsentDocumentType)}
              disabled={!canWrite}
            >
              <SelectTrigger id="consent-doc-type" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((dt) => (
                  <SelectItem key={dt} value={dt}>
                    {t(`editor.document_type_${dt}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="consent-language"
              className="text-sm font-medium"
            >
              {t("editor.language_label")}
            </label>
            <Select
              value={language}
              onValueChange={(v) => setLanguage(v as ConsentLanguage)}
              disabled={!canWrite}
            >
              <SelectTrigger id="consent-language" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lng) => (
                  <SelectItem key={lng} value={lng}>
                    {t(`editor.language_${lng}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label htmlFor="consent-text-md" className="text-sm font-medium">
              {t("editor.text_label")}
            </label>
            <Textarea
              id="consent-text-md"
              aria-label={t("editor.text_label")}
              className="min-h-[400px] font-mono text-sm"
              placeholder={t("editor.text_placeholder")}
              value={textMd}
              onChange={(e) => setTextMd(e.target.value)}
              disabled={!canWrite}
            />
          </div>

          {/* Text rules — Ley-8968: ≥10 disclosures (BUG-022 strict); Ley-9877 video:
              non-empty-text floor only (BUG-084). */}
          {isApplicationConsent ? (
            <div className="text-sm">
              <span
                data-testid="disclosure-count"
                className={
                  enoughDisclosures
                    ? "font-semibold text-[var(--talenta-success,#15803d)]"
                    : "font-semibold text-[var(--talenta-danger,#dc2626)]"
                }
              >
                {disclosureCount}
              </span>{" "}
              <span className="text-[var(--talenta-text-muted)]">
                {t("editor.disclosures_counter", { count: disclosureCount })}
              </span>
              {!enoughDisclosures && (
                <p
                  role="alert"
                  className="mt-1 text-[var(--talenta-danger,#dc2626)]"
                >
                  {t("editor.disclosures_insufficient")}
                </p>
              )}
            </div>
          ) : (
            !textRulesPass && (
              <p
                role="alert"
                data-testid="video-text-required"
                className="text-sm text-[var(--talenta-danger,#dc2626)]"
              >
                {t("editor.video_text_required")}
              </p>
            )
          )}

          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              data-testid="save-version-btn"
              disabled={saveDisabled}
              onClick={handleSave}
              className="min-h-[44px]"
            >
              {createMutation.isPending
                ? t("editor.saving")
                : t("editor.save")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleDiscard}
              disabled={!canWrite || createMutation.isPending}
              className="min-h-[44px]"
            >
              {t("editor.cancel")}
            </Button>
          </div>
        </div>

        {/* Preview column */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold">
            {t("editor.preview_label")}
          </h3>
          <ConsentVersionPreview
            textMd={textMd}
            emptyLabel={t("editor.preview_empty")}
          />
        </div>
      </div>

      {/* Version history grouped by language */}
      <Card>
        <CardContent className="py-4">
          <details>
            <summary className="cursor-pointer text-base font-semibold select-none">
              {t("version_history.title")}
            </summary>
            <div className="mt-3 space-y-4">
              {versionsQuery.isLoading && (
                <p className="text-sm text-[var(--talenta-text-muted)]">
                  {t("version_history.loading")}
                </p>
              )}
              {!versionsQuery.isLoading &&
                (versionsQuery.data?.versions.length ?? 0) === 0 && (
                  <p className="text-sm text-[var(--talenta-text-muted)]">
                    {t("version_history.empty")}
                  </p>
                )}
              {LANGUAGES.filter((lng) => versionsByLanguage[lng]?.length).map(
                (lng) => (
                  <div key={lng} className="space-y-1">
                    <h4 className="text-sm font-semibold uppercase">
                      {t(`editor.language_${lng}`)}
                    </h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[var(--talenta-text-muted)]">
                          <th className="py-1 pr-2">
                            {t("version_list.header_version")}
                          </th>
                          <th className="py-1 pr-2">
                            {t("version_list.header_status")}
                          </th>
                          <th className="py-1 pr-2">
                            {t("version_list.header_created")}
                          </th>
                          <th className="py-1">
                            {t("version_list.header_actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {versionsByLanguage[lng].map((v) => (
                          <tr key={v.id} className="border-t border-[var(--talenta-border)]">
                            <td className="py-1 pr-2">
                              v{v.version_number}
                              <span className="ml-1 text-xs text-[var(--talenta-text-muted)]">
                                {t(`editor.document_type_short_${v.document_type}`)}
                              </span>
                            </td>
                            <td className="py-1 pr-2">
                              {v.status === "active"
                                ? t("version_list.status_active")
                                : t("version_list.status_superseded")}
                            </td>
                            <td className="py-1 pr-2">
                              {new Date(v.created_at).toLocaleDateString(
                                i18n.language,
                              )}
                            </td>
                            <td className="py-1">
                              <Button
                                type="button"
                                variant="ghost"
                                className="h-8 px-2"
                                onClick={() => setViewVersion(v)}
                              >
                                {t("version_list.view")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ),
              )}
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Read-only view of a historical version */}
      <Dialog
        open={viewVersion !== null}
        onClose={() => setViewVersion(null)}
        title={
          viewVersion
            ? `${t(`editor.language_${viewVersion.language_code}`)} · v${viewVersion.version_number}`
            : undefined
        }
      >
        {viewVersion && (
          <ConsentVersionPreview
            textMd={viewVersion.text_md}
            emptyLabel={t("editor.preview_empty")}
          />
        )}
      </Dialog>
    </div>
  );
}
